import { Component, ChangeDetectionStrategy, input, output, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SponsorContract, ClubShieldConfig } from '../../../core/models/types';
import { ClubShieldComponent } from '../club-shield/club-shield.component';

export interface JerseyClubInfo {
  id?: string;
  name: string;
  code: string;
  color: string;
  isUser?: boolean;
  shield?: ClubShieldConfig | null;
}

export type SponsorBrandType = 'adidas' | 'novasport' | 'novasport2' | 'pladur' | 'emirates' | 'custom' | 'none';

@Component({
  selector: 'app-club-jersey',
  standalone: true,
  imports: [CommonModule, ClubShieldComponent],
  templateUrl: './club-jersey.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClubJerseyComponent {
  readonly club = input<JerseyClubInfo | null>(null);
  readonly shield = input<ClubShieldConfig | null | undefined>(undefined);
  readonly sponsor = input<SponsorContract | null>(null);
  readonly isUser = input<boolean>(false);
  readonly variant = input<'home' | 'away'>('home');
  readonly size = input<'sm' | 'md' | 'lg' | 'xl'>('lg');

  readonly contractClick = output<void>();

  private static instanceCounter = 0;
  readonly uniqueId = `jersey-${++ClubJerseyComponent.instanceCounter}`;

  readonly containerSizeClasses = computed<string>(() => {
    switch (this.size()) {
      case 'sm':
        return 'w-20 h-20 sm:w-24 sm:h-24';
      case 'md':
        return 'w-24 h-24 sm:w-28 sm:h-28';
      case 'xl':
        return 'w-36 h-36 sm:w-44 sm:h-44';
      case 'lg':
      default:
        return 'w-32 h-32 sm:w-36 sm:h-36';
    }
  });

  readonly clubColor = computed<string>(() => {
    return this.club()?.color || '#0ea5e9';
  });

  readonly effectiveShield = computed<ClubShieldConfig | null | undefined>(() => {
    return this.shield() || this.club()?.shield;
  });

  readonly clubCode = computed<string>(() => {
    const raw = this.club()?.code;
    if (raw && raw.length <= 4) return raw.toUpperCase();
    const name = this.club()?.name || '';
    if (name.length >= 3) return name.slice(0, 3).toUpperCase();
    return 'FC';
  });

  readonly sponsorName = computed<string>(() => {
    const sp = this.sponsor() as any;
    return sp?.sponsorName || sp?.name || '';
  });

  readonly brandType = computed<SponsorBrandType>(() => {
    const name = this.sponsorName().toLowerCase();
    if (!name) return 'none';
    if (name.includes('adidas')) return 'adidas';
    if (name.includes('nova sport 2') || name.includes('nova sport ii')) return 'novasport2';
    if (name.includes('nova sport') || name.includes('novasport')) return 'novasport';
    if (name.includes('pladur') || name.includes('oscar') || name.includes('instalaciones')) return 'pladur';
    if (name.includes('emirates') || name.includes('fly')) return 'emirates';
    return 'custom';
  });

  readonly logoUrl = computed<string | null>(() => {
    const sp = this.sponsor() as any;
    const url = sp?.logoUrl;
    if (!url) return null;
    const trimmed = url.trim();
    if (!trimmed) return null;
    let finalUrl = trimmed;
    if (
      !trimmed.startsWith('http://') &&
      !trimmed.startsWith('https://') &&
      !trimmed.startsWith('data:') &&
      !trimmed.startsWith('/')
    ) {
      finalUrl = '/' + trimmed;
    }
    // Append v=2 for local svg assets so updated viewBox is immediately fetched by browser
    if (finalUrl.startsWith('/assets/') && finalUrl.endsWith('.svg')) {
      return `${finalUrl}?v=2`;
    }
    return finalUrl;
  });

  onJerseyClick(): void {
    if (this.isUser()) {
      this.contractClick.emit();
    }
  }
}
